import type { AxiosError } from 'axios';
import { Button, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import {
  getGetContractQueryKey,
  getListContractLinesQueryKey,
  useAddContractLine
} from '@/api/generated/contracts/contracts';
import { useListUnits } from '@/api/generated/units/units';
import {
  CreatableSelectField,
  FormModal,
  ModalFooter,
  MutationButton,
  NumberField,
  SelectField,
  TextField,
  Toast
} from '@/components/common';
import { activeUnitOptions } from '@/features/units/unitDisplay';

const schema = z.object({
  itemCode: z.string().min(1),
  category: z.string().min(1),
  unitId: z.string().min(1),
  description: z.string().min(1),
  // 0 dopušteno (okvirni ugovori) — vidi ContractLine.quantity/backend AddContractLineRequest.
  quantity: z.number().int().min(0),
  unitPrice: z.number().positive(),
  normHours: z.number().nullable()
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  itemCode: '',
  category: '',
  unitId: '',
  description: '',
  quantity: 0,
  unitPrice: 0,
  normHours: null
};

// Isti obrazac kao OfferLineFormModal — backend već vraća specifičan, lokalizirani razlog.
const lineErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface ContractLineFormModalProps {
  opened: boolean;
  onClose: () => void;
  contractId: string;
  existingCategories?: string[];
  onSaved?: (message: string) => void;
}

/**
 * Ručni unos stavke — koristi se kad ugovor NIJE vezan na cjenik
 * (contract.priceListId prazan), pa se stavke ne mogu birati iz cjenika
 * (ContractItemPickerModal). Backend (AddContractLineRequest/ContractService.addLine)
 * već podržava ovaj put bez priceListItemId.
 */
export const ContractLineFormModal = ({
  opened,
  onClose,
  contractId,
  existingCategories = [],
  onSaved
}: ContractLineFormModalProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const { data: unitsData } = useListUnits();
  const unitOptions = activeUnitOptions(unitsData, i18n.language);

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: emptyValues
  });

  useEffect(() => {
    if (opened) form.setValues(emptyValues);
  }, [opened]);

  const addMutation = useAddContractLine({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListContractLinesQueryKey(contractId) });
        queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contractId) });
        onSaved?.(t('contractLineForm.addSuccess'));
        onClose();
      }
    }
  });

  const handleSubmit = (values: FormValues) => {
    addMutation.mutate({
      id: contractId,
      data: {
        itemCode: values.itemCode,
        category: values.category,
        description: values.description,
        unitId: values.unitId,
        quantity: values.quantity,
        unitPrice: values.unitPrice,
        normHours: values.normHours ?? undefined
      }
    });
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
            {t('contractLineForm.title')}
          </Text>
          <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
            {t('contractLineForm.intro')}
          </Text>
        </Stack>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={16}>
          {addMutation.isError && (
            <Toast tone="danger">{lineErrorMessage(addMutation.error, t('common.error'))}</Toast>
          )}

          <TextField
            label={t('contractLineForm.fieldItemCode')}
            required
            {...form.getInputProps('itemCode')}
          />

          <div className="form-row-2col">
            <div style={{ flex: 1 }}>
              <CreatableSelectField
                label={t('contractLineForm.fieldCategory')}
                required
                placeholder={t('contractLineForm.categoryPlaceholder')}
                data={existingCategories}
                createLabel={(query) => t('contractLineForm.categoryCreate', { value: query })}
                emptyLabel={t('contractLineForm.categoryEmpty')}
                {...form.getInputProps('category')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <SelectField
                label={t('contractLineForm.fieldUnit')}
                required
                allowDeselect={false}
                placeholder={t('priceLists.unitPlaceholder')}
                data={unitOptions}
                {...form.getInputProps('unitId')}
              />
            </div>
          </div>

          <TextField
            label={t('contractLineForm.fieldDescription')}
            required
            {...form.getInputProps('description')}
          />

          <div className="form-row-2col">
            <div style={{ flex: 1 }}>
              <NumberField
                label={t('contractLineForm.fieldQuantity')}
                required
                allowNegative={false}
                allowDecimal={false}
                min={0}
                {...form.getInputProps('quantity')}
              />
            </div>
            <div style={{ flex: 1 }}>
              <NumberField
                label={t('contractLineForm.fieldUnitPrice')}
                required
                allowNegative={false}
                decimalScale={2}
                fixedDecimalScale
                {...form.getInputProps('unitPrice')}
              />
            </div>
          </div>

          <NumberField
            label={t('contractLineForm.fieldNormHours')}
            allowNegative={false}
            decimalScale={2}
            {...form.getInputProps('normHours')}
          />

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              type="submit"
              isPending={addMutation.isPending}
              isPaused={addMutation.isPaused}
            >
              {t('contractLineForm.saveButton')}
            </MutationButton>
          </ModalFooter>
        </Stack>
      </form>
    </FormModal>
  );
};
